import SidePanel from '~/components/SidePanel';
import TextInput from '~/components/TextInput';
import { useEffect, useState } from 'react';
import { formatDateTime } from '~/lib/DateTime.js';
import CompactBookableResourcesSelect from '~/components/scheduling/CompactBookableResourcesSelect';
import TextArea from '~/components/TextArea';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import useDelete from '~/lib/useDelete';
import * as Toast from '~/lib/Toast';
import StartEndDateTime from '~/components/forms/StartEndDateTime.jsx';
import DateTimeInput from '~/components/DateTimeInput.jsx';
import { useBlockDetailsQuery } from '~/lib/Queries';
import Loading from '~/components/Loading';

const BlockTimePanel = ({
    isOpen,
    onClose,
    start: initialStart,
    end: initialEnd,
    resourceId,
    block,
    onSuccess,
}) => {
    const [name, setName] = useState('');
    const [selectedResources, setSelectedResources] = useState([]);
    const [description, setDescription] = useState('');
    const [start, setStart] = useState(initialStart);
    const [end, setEnd] = useState(initialEnd);
    const [isEditingTime, setIsEditingTime] = useState(false);

    const { data: blockDetails, isLoading } = useBlockDetailsQuery(block?.id);

    const { save, isSaving } = useBusinessPostApi(
        `/calendar/blocks/${block?.id || 0}`,
    );

    const { saveDelete, isDeleting } = useDelete(
        block?.id ? `/calendar/blocks/${block.id}/delete` : null,
    );

    useEffect(() => {
        if (!isOpen) {
            setIsEditingTime(false);
            return;
        }

        if (!block) {
            setName('');
            setDescription('');
            if (resourceId) setSelectedResources([parseInt(resourceId)]);
            setStart(initialStart);
            setEnd(initialEnd);
            setIsEditingTime(true);
        } else {
            setName(block.name);
            if (block.calendar?.bookable_resources) {
                setSelectedResources(
                    block.calendar?.bookable_resources?.map(r => r.id),
                );
            }
            // The calendar events list hides start/end on blocking events, so
            // fall back to the client_start/client_end it does return
            setStart(block.client_start || block.start);
            setEnd(block.client_end || block.end);
            setDescription(block.description || '');
            setIsEditingTime(false);
        }
    }, [isOpen, block]);

    useEffect(() => {
        // Fill in the details the calendar events list leaves out once loaded
        if (!isOpen || !block || !blockDetails) return;

        if (blockDetails.bookable_resources) {
            setSelectedResources(blockDetails.bookable_resources.map(r => r.id));
        }
        if (blockDetails.block?.start) setStart(blockDetails.block.start);
        if (blockDetails.block?.end) setEnd(blockDetails.block.end);
        setDescription(blockDetails.block?.description || '');
    }, [isOpen, block, blockDetails]);

    function doSave() {
        save({
            name,
            description,
            resources: selectedResources.filter(r => !!r),
            start,
            end,
        }).then(result => {
            Toast.success('Successfully blocked off time');
            onClose();
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        });
    }

    function doDelete() {
        saveDelete().then(() => {
            Toast.success('Successfully deleted blocked time');
            onClose();
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        });
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title="Block Time"
            onCancel={onClose}
            saveLabel={isSaving ? 'Saving...' : 'Save'}
            showSave={true}
            onSave={doSave}
        >
            <TextInput
                onChange={setName}
                value={name}
                label="Name"
                id="name"
                autoFocus={true}
                className="mb-4"
            />

            <div className="panel-label">Date &amp; Time</div>
            <div className="panel p-4 mb-4">
                {!isEditingTime && (
                    <div className="flex items-center justify-between">
                        <div>
                            <StartEndDateTime start={start} end={end} />
                        </div>
                        <button
                            type="button"
                            className="btn-light-pill-sm uppercase"
                            onClick={() => setIsEditingTime(true)}
                        >
                            Edit
                        </button>
                    </div>
                )}
                {isEditingTime && (
                    <div className="flex justify-between">
                        <div className="flex-1">
                            <DateTimeInput
                                value={start}
                                onChange={setStart}
                                className=" flex-col"
                                dateLabel="Start"
                                timeLabel=" "
                                id="start"
                            />
                        </div>
                        <div className="mx-4 pt-12">-</div>
                        <div className="flex-1">
                            <DateTimeInput
                                value={end}
                                onChange={setEnd}
                                className=" flex-col"
                                dateLabel="End"
                                timeLabel=" "
                                id="end"
                            />
                        </div>
                    </div>
                )}
            </div>

            {isLoading && <Loading className="mb-4" />}
            {!isLoading && (
                <CompactBookableResourcesSelect
                    label="Block On"
                    value={selectedResources}
                    onChange={setSelectedResources}
                    dontDefaultAll={true}
                    initialOpen={!block?.id}
                    hideIfOneType={true}
                    className="mb-4"
                    id="resources"
                />
            )}

            <TextArea
                onChange={setDescription}
                value={description}
                label="Notes"
                className="mb-4"
            />

            {block?.id && (
                <button
                    type="button"
                    className="btn-pink-pill"
                    onClick={doDelete}
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Deleting...' : 'Delete Blocked Time'}
                </button>
            )}
        </SidePanel>
    );
};

export default BlockTimePanel;
